import { Listbox } from "@headlessui/react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  pickDefaultLanguage,
  useBranding,
} from "../../store/branding-context";
import AuthContext from "../../store/auth-context";
import globeIcon from "../../resources/icons/Globe.svg";
import chevronDownIcon from "../../resources/icons/chevron-down.svg";

const DEFAULT_LANGUAGES = [
  { value: "en", label: "English" },
  { value: "pt", label: "Português" },
];

const LandingLanguageSelect = () => {
  const { i18n, t } = useTranslation();
  const { organisationData } = useBranding();
  const authContext = useContext(AuthContext);

  const languages = useMemo(() => {
    if (organisationData?.languages) {
      return Object.entries(organisationData.languages).map(([code, name]) => ({
        value: code,
        label: name,
      }));
    }

    return DEFAULT_LANGUAGES;
  }, [organisationData?.languages]);

  const [selectedLanguage, setSelectedLanguage] = useState(
    languages[0]?.value || "en",
  );

  const applyLanguage = (nextLanguage) => {
    i18n.changeLanguage(nextLanguage);
    authContext.setSelectedLanguage(nextLanguage);
    document.documentElement.lang = nextLanguage;
  };

  useEffect(() => {
    const availableLanguages = languages.map((language) => language.value);
    const portalDefault = pickDefaultLanguage(availableLanguages);
    const savedLanguage =
      localStorage.getItem("selectedLanguage") ||
      localStorage.getItem("i18nextLng");
    const resolvedLanguage = availableLanguages.includes(savedLanguage)
      ? savedLanguage
      : portalDefault;

    setSelectedLanguage(resolvedLanguage);

    if (
      i18n.language !== resolvedLanguage ||
      authContext.selectedLanguage !== resolvedLanguage ||
      document.documentElement.lang !== resolvedLanguage
    ) {
      applyLanguage(resolvedLanguage);
    }
  }, [authContext.selectedLanguage, i18n.language, languages]);

  const handleLanguageChange = (nextLanguage) => {
    setSelectedLanguage(nextLanguage);
    applyLanguage(nextLanguage);
  };

  const selectedLabel =
    languages.find((language) => language.value === selectedLanguage)?.label ||
    selectedLanguage.toUpperCase();

  return (
    <Listbox value={selectedLanguage} onChange={handleLanguageChange}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button
            aria-label={t("authPages:select_language")}
            className="inline-flex h-[34px] min-w-[100px] items-center gap-2 rounded-lg border border-cfSignInSignUpInputBorder bg-cfSecondary px-3 text-xs font-semibold text-cfSignInSignUpHeadingText focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
          >
            <img
              src={globeIcon}
              alt=""
              className="size-[14px] shrink-0"
              aria-hidden="true"
            />
            <span className="min-w-[20px] text-left">{selectedLabel}</span>
            <img
              src={chevronDownIcon}
              alt=""
              className={`size-[10px] shrink-0 transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            />
          </Listbox.Button>

          <Listbox.Options className="absolute right-0 z-20 mt-1.5 min-w-full overflow-hidden rounded-lg border border-cfSignInSignUpInputBorder bg-cfSecondary shadow-[0_8px_24px_rgba(16,24,40,0.12)] focus:outline-none">
            {languages.map((language) => (
              <Listbox.Option
                key={language.value}
                value={language.value}
                className={({ active, selected }) =>
                  `cursor-pointer px-3 py-2 text-xs font-semibold ${
                    active || selected
                      ? "bg-cfSearchContainer text-cfPrimary"
                      : "text-cfSignInSignUpHeadingText"
                  }`
                }
              >
                {language.label}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      )}
    </Listbox>
  );
};

export default LandingLanguageSelect;
